/**
 * Account List View
 *
 * Matches Client list view: view dropdown, search, Filter/Export/Edit View,
 * ListViewTable, Pagination. Account UI follows Client UI patterns.
 */

import React, { useState, useMemo } from 'react';
import { Plus, ChevronDown, Filter, Download, Settings, Search, X } from 'lucide-react';
import { ListViewTable } from './ListViewTable';
import { Pagination } from './Pagination';
import { FilterPanel, type FilterCondition } from './FilterPanel';
import { EditViewModal } from './EditViewModal';
import { AccountModal } from '../modals/AccountModal';
import {
  useAccountListViews,
  useAccountListViewData,
  useDefaultAccountListView,
  useUpdateAccountListView,
} from '../../services/queries/accountListViewQueries';
import type { ListViewRecord, ListView } from '../../types/listView';
import { useToast } from '../../contexts/ToastContext';

interface AccountListViewProps {
  onNavigateToAccount: (accountId: string) => void;
  onCreateAccount?: () => void;
}

export const AccountListView: React.FC<AccountListViewProps> = ({
  onNavigateToAccount,
  onCreateAccount,
}) => {
  const { success } = useToast();
  const { data: defaultView } = useDefaultAccountListView();
  const [selectedViewId, setSelectedViewId] = useState<string>(defaultView?.id || 'lv_accounts_all');
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, FilterCondition>>({});
  const [isEditViewModalOpen, setIsEditViewModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<ListViewRecord | null>(null);

  const { data: listViews, isLoading: loadingViews } = useAccountListViews();
  const { data: listViewData, isLoading: loadingData, isFetching, isPlaceholderData } = useAccountListViewData(selectedViewId, {
    page: currentPage - 1,
    pageSize,
  });
  const isFetchingPage = isFetching && isPlaceholderData;
  const updateListView = useUpdateAccountListView();

  React.useEffect(() => {
    if (defaultView && !selectedViewId) setSelectedViewId(defaultView.id);
  }, [defaultView, selectedViewId]);

  const currentViewName = listViews?.find((v) => v.id === selectedViewId)?.name || 'All Accounts';

  const filteredRecords = useMemo(() => {
    if (!listViewData?.records) return [];
    let records = listViewData.records;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const visibleColumns = listViewData.listView.columns.filter((col) => col.isVisible);
      records = records.filter((record) =>
        visibleColumns.some((col) =>
          String(record[col.fieldApiName] ?? '').toLowerCase().includes(query)
        )
      );
    }
    if (Object.keys(activeFilters).length > 0) {
      records = records.filter((record) =>
        Object.entries(activeFilters).every(([fieldName, filter]) => {
          const recordValue = record[fieldName];
          if (recordValue === null || recordValue === undefined) return false;
          switch (filter.operator) {
            case 'equals':
              return String(recordValue).toLowerCase() === String(filter.value).toLowerCase();
            case 'notEquals':
              return String(recordValue).toLowerCase() !== String(filter.value).toLowerCase();
            case 'contains':
              return String(recordValue).toLowerCase().includes(String(filter.value).toLowerCase());
            case 'notContains':
              return !String(recordValue).toLowerCase().includes(String(filter.value).toLowerCase());
            case 'startsWith':
              return String(recordValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
            case 'endsWith':
              return String(recordValue).toLowerCase().endsWith(String(filter.value).toLowerCase());
            case 'greaterThan':
              return Number(recordValue) > Number(filter.value);
            case 'lessThan':
              return Number(recordValue) < Number(filter.value);
            case 'greaterOrEqual':
              return Number(recordValue) >= Number(filter.value);
            case 'lessOrEqual':
              return Number(recordValue) <= Number(filter.value);
            default:
              return true;
          }
        })
      );
    }
    return records;
  }, [listViewData, searchQuery, activeFilters]);

  const sortedRecords = useMemo(() => {
    if (!sortConfig) return filteredRecords;
    return [...filteredRecords].sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredRecords, sortConfig]);

  const totalRecords = listViewData?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const recordsToShow = sortedRecords;

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedViewId, activeFilters]);

  const handleRecordClick = (record: ListViewRecord) => onNavigateToAccount(record.id);
  const handleViewChange = (viewId: string) => {
    setSelectedViewId(viewId);
    setIsViewDropdownOpen(false);
  };

  const handleExport = () => {
    if (!listViewData) return;
    const columns = listViewData.listView.columns
      .filter((col) => col.isVisible)
      .sort((a, b) => a.order - b.order);
    const header = columns.map((col) => col.fieldLabel).join(',');
    const rows = sortedRecords.map((record) =>
      columns.map((col) => `"${String(record[col.fieldApiName] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Accounts_${listViewData.listView.name}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    success('Export successful', `Downloaded ${sortedRecords.length} records (current page)`);
  };

  const handleNewAccount = () => {
    if (onCreateAccount) {
      onCreateAccount();
    } else {
      setSelectedAccount(null);
      setIsAccountModalOpen(true);
    }
  };

  const handleSaveListView = (updates: Partial<ListView>) => {
    if (!listViewData) return;
    updateListView.mutate(
      { id: listViewData.listView.id, updates },
      {
        onSuccess: () => {
          setIsEditViewModalOpen(false);
          success('View updated', 'List view settings have been saved');
        },
        onError: (err) => console.error('Failed to update list view:', err),
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f172a] p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Accounts</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage firm and individual accounts</p>
          </div>
          <button
            onClick={handleNewAccount}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Account
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-center gap-4">
        <div className="relative">
          <button
            onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            disabled={loadingViews}
          >
            <span className="font-medium">{currentViewName}</span>
            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
          {isViewDropdownOpen && listViews && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsViewDropdownOpen(false)} />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-20">
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    List Views
                  </div>
                  {listViews.map((view) => (
                    <button
                      key={view.id}
                      onClick={() => handleViewChange(view.id)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[#334155] transition-colors ${
                        view.id === selectedViewId
                          ? 'bg-gray-100 dark:bg-[#334155] text-gray-900 dark:text-white font-medium'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{view.name}</span>
                        {view.isDefault && (
                          <span className="text-xs text-gray-500 dark:text-gray-500">(Default)</span>
                        )}
                      </div>
                      {view.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {view.description}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search accounts..."
            className="w-full pl-10 pr-10 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFilterPanelOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Filter records"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filter</span>
            {Object.keys(activeFilters).length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {Object.keys(activeFilters).length}
              </span>
            )}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => setIsEditViewModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Edit list view"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Edit View</span>
          </button>
        </div>
      </div>

      {listViewData && (
        <div className="relative">
          <ListViewTable
            listView={listViewData.listView}
            records={recordsToShow}
            onRecordClick={handleRecordClick}
            isLoading={loadingData}
            sortConfig={sortConfig}
            onSort={(field: string) => {
              setSortConfig((prev) => {
                if (!prev || prev.field !== field) return { field, direction: 'asc' };
                if (prev.direction === 'asc') return { field, direction: 'desc' };
                return null;
              });
            }}
          />
          {isFetchingPage && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-900/70 z-10" aria-busy="true" aria-label="Loading page">
              <div className="flex flex-col items-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading page {currentPage}...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {(listViewData?.totalRecords ?? 0) > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      {!listViewData && loadingData && (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading accounts...</p>
          </div>
        </div>
      )}

      {isEditViewModalOpen && listViewData && (
        <EditViewModal
          listView={listViewData.listView}
          onSave={handleSaveListView}
          onClose={() => setIsEditViewModalOpen(false)}
        />
      )}

      {isFilterPanelOpen && listViewData && (
        <FilterPanel
          fields={listViewData.listView.columns.map((col) => ({
            fieldApiName: col.fieldApiName,
            fieldLabel: col.fieldLabel,
            fieldType: col.fieldType as 'text' | 'number' | 'date' | 'datetime' | 'picklist' | 'boolean',
          }))}
          activeFilters={activeFilters}
          onApply={(filters) => {
            setActiveFilters(filters);
            setIsFilterPanelOpen(false);
          }}
          onClose={() => setIsFilterPanelOpen(false)}
        />
      )}

      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setIsAccountModalOpen(false);
          setSelectedAccount(null);
        }}
        account={selectedAccount ? { id: selectedAccount.id, name: selectedAccount.name as string, type: (selectedAccount.type as 'firm' | 'individual') ?? 'firm', description: selectedAccount.description as string, accountOwner: selectedAccount.accountOwner as string, phone: selectedAccount.phone as string, email: selectedAccount.email as string, billingAddress: selectedAccount.billingAddress as string } : null}
      />
    </div>
  );
};
